import { memo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, FolderPlus, X } from 'lucide-react';
import { useBookCoverUrl } from '../hooks/useBookCoverUrl';
import { useLazyImage } from '../hooks/useLazyImage';
import { useModalStore } from '../store/modalStore';
import type { Book } from '../types/models';
import type { ReadingProgress } from '../lib/offlineStorage';

interface BookCardProps {
  book: Book;
  style?: React.CSSProperties;
  readingProgress?: ReadingProgress | null;
  showRemoveButton?: boolean;
  onRemove?: (e: React.MouseEvent) => void;
  isRemoving?: boolean;
  hasAnyCollection?: boolean;
  gridItemProps?: {
    ref: (el: HTMLElement | null) => void;
    tabIndex: number;
    onFocus: () => void;
    'data-grid-index': number;
  };
}

function BookCardComponent({ book, style, readingProgress, showRemoveButton, onRemove, isRemoving, hasAnyCollection, gridItemProps }: BookCardProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const openModal = useModalStore((state) => state.openModal);
  const hasCoverPath = !!book.coverPath;
  const { ref, isInView, isLoaded, isError, onLoad, onError } = useLazyImage(
    hasCoverPath ? `/api/books/${book.id}/cover` : null,
    { rootMargin: '300px' }
  );
  const coverUrl = useBookCoverUrl(book.id, hasCoverPath && isInView, 'thumb');

  const publishedYear = book.publishedDate
    ? new Date(book.publishedDate).getFullYear()
    : null;
  const authorNames = book.authors?.map((a) => a.name).join(', ');

  const showPlaceholder = !coverUrl || isError;
  const showSkeleton = coverUrl && isInView && !isLoaded && !isError;
  const showImage = coverUrl && isInView && !isError;

  return (
    <article
      className="book-card"
      style={style}
      ref={gridItemProps?.ref}
      data-grid-index={gridItemProps?.['data-grid-index']}
    >
      {showRemoveButton && onRemove && (
        <button
          className="book-card-remove"
          onClick={onRemove}
          disabled={isRemoving}
          title="Remove from collection"
          aria-label="Remove from collection"
        >
          <X className="icon icon-sm" aria-hidden="true" />
        </button>
      )}
      {hasAnyCollection !== undefined && (
        <button
          ref={triggerRef}
          className="book-card-add-collection"
          onClick={(e) => {
            e.stopPropagation();
            if (!hasAnyCollection) return;
            openModal('addToCollection', { book }, undefined, triggerRef);
          }}
          onKeyDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          disabled={!hasAnyCollection}
          title={hasAnyCollection ? 'Add to collection' : 'Create a collection first — go to the Lists page'}
          aria-label={hasAnyCollection ? 'Add to collection' : 'No collections yet — create one on the Lists page'}
        >
          <FolderPlus className="icon icon-sm" aria-hidden="true" />
        </button>
      )}
      <Link
        to={`/books/${book.id}`}
        className="book-card-link"
        tabIndex={gridItemProps?.tabIndex}
        onFocus={gridItemProps?.onFocus}
      >
        <div className="book-cover" ref={ref as React.RefObject<HTMLDivElement>}>
          {showSkeleton && <div className="book-cover-skeleton" />}

          {showImage && (
            <img
              src={coverUrl}
              alt={`Cover of ${book.title}`}
              onLoad={onLoad}
              onError={onError}
              className={isLoaded ? 'loaded' : ''}
            />
          )}

          {showPlaceholder && (
            <div className="book-cover-placeholder">
              <BookOpen className="icon icon-lg" aria-hidden="true" />
            </div>
          )}
          {readingProgress && readingProgress.percentage > 0 && (
            <div
              className="book-progress-bar"
              role="progressbar"
              aria-valuenow={Math.round(readingProgress.percentage)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${Math.round(readingProgress.percentage)}% read`}
            >
              <div
                className="book-progress-fill"
                style={{ width: `${readingProgress.percentage}%` }}
              />
            </div>
          )}
        </div>
        <div className="book-info">
          <h3 className="book-title">{book.title}</h3>
          {authorNames && <p className="book-author">{authorNames}</p>}
          {publishedYear && <p className="book-year">{publishedYear}</p>}
          <div className="book-meta">
            {book.format && (
              <span className="book-format">{book.format.toUpperCase()}</span>
            )}
            {book.drmStatus && book.drmStatus !== 'none' && (
              <span className="book-drm-status">{book.drmStatus}</span>
            )}
            {readingProgress && readingProgress.percentage > 0 && (
              <span className="book-progress-text">
                {Math.round(readingProgress.percentage)}%
              </span>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
}

export const BookCard = memo(BookCardComponent, (prevProps, nextProps) => {
  return (
    prevProps.book.id === nextProps.book.id &&
    prevProps.book.coverPath === nextProps.book.coverPath &&
    prevProps.book.title === nextProps.book.title &&
    prevProps.book.publishedDate === nextProps.book.publishedDate &&
    prevProps.showRemoveButton === nextProps.showRemoveButton &&
    prevProps.isRemoving === nextProps.isRemoving &&
    prevProps.hasAnyCollection === nextProps.hasAnyCollection
  );
});
